import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated", { description: "Sign in with your new password." });
      navigate("/login");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Enter a new password for your account."
      footer={
        <>
          Changed your mind?{" "}
          <Link to="/login" data-testid="reset-back-to-login" className="text-klein font-semibold hover:underline">
            Back to sign in
          </Link>
        </>
      }
    >
      {!token ? (
        <div className="text-center py-4" data-testid="reset-no-token">
          <p className="text-sm text-bad font-medium">This reset link is missing its token.</p>
          <p className="text-gray-500 text-sm mt-1">Please use the link from your email, or request a new one.</p>
          <Link to="/forgot-password" className="inline-block mt-4 text-klein font-semibold hover:underline text-sm">
            Request a new link
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              data-testid="reset-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1.5"
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              data-testid="reset-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1.5"
              placeholder="Re-enter your new password"
            />
          </div>
          {error && <p data-testid="reset-error" className="text-sm text-bad font-medium">{error}</p>}
          <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full bg-[#0A0A0A] hover:bg-gray-800 py-6 text-base">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
